import React, { useState, useEffect } from 'react';
import { ProductPrice } from '../../types';
import { formatPrice } from '../../hooks/useProductPrices';

interface QuantityPriceSelectorProps {
  prices: ProductPrice[];
  onPriceChange: (price: number, quantity: number, originalPrice: number) => void;
  className?: string;
  pillQuantity?: number; // items per container (box/pack/strip/pen). 0 = quantity IS the item count
  unitLabel?: string; // singular label for the inner item: "Tablet", "Sachet", "Capsule", "Pen", etc.
  containerLabel?: string; // singular label for the outer container: "Box", "Pack", "Strip"
  labelSuffix?: (qty: number) => string; // optional per-quantity tail (e.g. weekly-dose annotation)
}

const pluralize = (word: string, n: number) => {
  if (n <= 1) return word;
  if (/(s|x|z|ch|sh)$/i.test(word)) return word + 'es';
  if (/y$/i.test(word) && !/[aeiou]y$/i.test(word)) return word.slice(0, -1) + 'ies';
  return word + 's';
};

const QuantityPriceSelector: React.FC<QuantityPriceSelectorProps> = ({
  prices,
  onPriceChange,
  className = '',
  pillQuantity = 0,
  unitLabel = 'Tablet',
  containerLabel = 'Box',
  labelSuffix,
}) => {
  // Smart label: if pillQuantity > 0 and quantities are small (≤10), show as containers
  // Otherwise show as direct unit count
  const maxQty = Math.max(...prices.map(p => p.quantity), 0);
  const isContainerMode = pillQuantity > 0 && maxQty <= 10;
  const formatQtyLabel = (qty: number) => {
    let base: string;
    if (isContainerMode) {
      const total = qty * pillQuantity;
      base = pillQuantity === 1
        ? `${qty} ${pluralize(unitLabel, qty)}`
        : `${qty} ${pluralize(containerLabel, qty)} (${total} ${pluralize(unitLabel, total)})`;
    } else {
      base = `${qty} ${pluralize(unitLabel, qty)}`;
    }
    return labelSuffix ? `${base}${labelSuffix(qty)}` : base;
  };
  const loading = false;
  const [selectedQuantity, setSelectedQuantity] = useState<number | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Reset selection when prices change (e.g. variant switch)
  useEffect(() => {
    if (prices.length > 0) {
      const sorted = [...prices].sort((a, b) => a.quantity - b.quantity);
      const firstOption = sorted[0];
      setSelectedQuantity(firstOption.quantity);
      onPriceChange(firstOption.sale_price, firstOption.quantity, firstOption.original_price);
    } else {
      setSelectedQuantity(null);
    }
    // intentionally omit onPriceChange from deps to avoid re-runs on function identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prices]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.dropdown-container')) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleQuantityChange = (quantity: number) => {
    const priceData = prices.find(p => p.quantity === quantity);

    if (priceData) {
      setSelectedQuantity(quantity);
      onPriceChange(priceData.sale_price, quantity, priceData.original_price);
      setIsDropdownOpen(false);
    }
  };

  const getSelectedOption = () => {
    const priceData = prices.find(p => p.quantity === selectedQuantity);
    return priceData ? `${formatQtyLabel(priceData.quantity)} - $${formatPrice(priceData.sale_price)}` : 'Select quantity';
  };

  const getCurrentPriceData = (): ProductPrice | null => {
    return prices.find(p => p.quantity === selectedQuantity) || null;
  };

  if (loading) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="animate-pulse bg-warm-200 h-10 rounded-lg"></div>
        <div className="animate-pulse bg-warm-200 h-16 rounded-lg"></div>
      </div>
    );
  }

  // If there's an error but prices loaded from fallback/cache, don't block the UI
  // The pricing display below will handle showing available data

  if (prices.length === 0) {
    return (
      <div className={`text-warm-600 p-4 bg-cream-100 rounded-lg ${className}`}>
        <p>No quantity-based pricing available for this product.</p>
      </div>
    );
  }

  const currentPrice = getCurrentPriceData();

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Custom Dropdown */}
      <div className="relative dropdown-container">
        <label className="block text-sm font-medium text-forest-700 mb-2">
          Select Quantity
        </label>

        {/* Selected option display button */}
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="w-full px-4 py-3 bg-cream-50 border border-warm-300 rounded-lg shadow-subtle flex items-center justify-between text-left hover:border-forest-400 focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 transition-all duration-200"
        >
          <span className="text-forest-900 font-medium">{getSelectedOption()}</span>
          <svg
            className={`w-5 h-5 text-warm-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown options */}
        {isDropdownOpen && (
          <div className="absolute z-50 w-full mt-1 bg-cream-50 border border-warm-200 rounded-lg shadow-elevated max-h-60 overflow-auto">
            {prices.map((priceData, index) => (
              <button
                key={priceData.quantity}
                onClick={() => handleQuantityChange(priceData.quantity)}
                className={`w-full px-4 py-3 text-left hover:bg-forest-50 focus:outline-none focus:bg-forest-50 transition-colors duration-150 ${
                  selectedQuantity === priceData.quantity ? 'bg-forest-100 text-forest-900' : 'text-forest-900'
                } ${index !== prices.length - 1 ? 'border-b border-warm-100' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{formatQtyLabel(priceData.quantity)}</span>
                  <div className="text-right">
                    <div className="font-bold text-forest-700 font-mono">${formatPrice(priceData.sale_price)}</div>
                    {priceData.discount_rate > 0 && (
                      <div className="text-xs text-rust-600">{priceData.discount_rate}% off</div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Price Information Display */}
      {currentPrice && (
        <div className="bg-cream-100 border border-warm-200 rounded-lg p-4">
          <div className="space-y-2">
            {/* Original Price */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-warm-600">List Price:</span>
              <span className="text-sm text-warm-500 line-through font-mono">
                ${formatPrice(currentPrice.original_price)}
              </span>
            </div>

            {/* Discount Rate */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-warm-600">Discount:</span>
              <span className="text-sm font-medium text-rust-600">
                {currentPrice.discount_rate}%
              </span>
            </div>

            {/* Sale Price */}
            <div className="flex justify-between items-center pt-2 border-t border-warm-200">
              <span className="font-medium text-forest-900">Sale Price:</span>
              <span className="text-xl font-bold text-forest-700 font-mono">
                ${formatPrice(currentPrice.sale_price)}
              </span>
            </div>

            {/* Savings */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-sage-600">You Save:</span>
              <span className="text-sm font-medium text-sage-600 font-mono">
                ${formatPrice(currentPrice.original_price - currentPrice.sale_price)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Quantity Price Comparison */}
      <div className="bg-cream-100 rounded-lg p-4">
        <h4 className="text-sm font-medium text-forest-700 mb-3">Price by Quantity</h4>
        <div className="space-y-2">
          {prices.map((priceData) => (
            <div
              key={priceData.quantity}
              className={`flex justify-between items-center p-2 rounded ${
                selectedQuantity === priceData.quantity
                  ? 'bg-forest-100 border border-forest-300'
                  : 'hover:bg-warm-100'
              }`}
            >
              <span className="text-sm font-medium text-forest-800">
                {formatQtyLabel(priceData.quantity)}
              </span>
              <div className="text-right">
                <div className="text-sm font-medium text-forest-900 font-mono">
                  ${formatPrice(priceData.sale_price)}
                </div>
                <div className="text-xs text-warm-500 font-mono">
                  ${(priceData.sale_price / priceData.quantity).toFixed(2)} / {unitLabel.toLowerCase()}
                </div>
                <div className="text-xs text-rust-600">
                  {priceData.discount_rate > 0 ? `${priceData.discount_rate}% off` : '\u00A0'}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 text-xs text-warm-400">
          Buy more to save more.
        </div>
      </div>
    </div>
  );
};

export default QuantityPriceSelector;
